import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SelectField from '@/components/common/SelectField';
import UploadColumnSelect from '@/components/upload/UploadColumnSelect';
import UploadLayerInfo from '@/components/upload/UploadLayerInfo';
import UploadSampleTable from '@/components/upload/UploadSampleTable';
import { UPLOAD_COLUMN_TYPES, UPLOAD_CRS_GROUPS } from '@/config/upload';
import { choicesProblem, initialChoices, isTableFormat, placeColumns, startRequest, UploadChoices } from '@/utils/upload';
import { Upload, UploadGeometryMode, UploadPreviewSheet, UploadStartRequest } from '@/types/upload';

interface UploadColumnCheckProps {
  upload: Upload;
  /** One sheet. Give the component key={sheet.name}, so the choices start over for another sheet. */
  sheet: UploadPreviewSheet;
  disabled: boolean;
  /**
   * The request the choices make, or null with the reason (an uploads.* key) while they are not complete; called on
   * every change. The dialog around the check holds the start button.
   */
  onChange: (request: UploadStartRequest | null, problem: string | null) => void;
}

const input = 'w-full h-8 px-2 bg-gn-input border border-gn-input rounded-md text-xs text-gn-primary disabled:opacity-50';
const wide = 'w-full h-9 px-3 bg-gn-input border border-gn-input rounded-md text-sm text-gn-primary disabled:opacity-50';

/**
 * The column check of one sheet: the layer's name, how the rows' places are given (two coordinate columns and their
 * coordinate system, a WKT column, the file's own geometry, or none - the company's data without a map), and per
 * column whether it comes in, under which name and as what.
 */
const UploadColumnCheck: React.FC<UploadColumnCheckProps> = ({ upload, sheet, disabled, onChange }) => {
  const { t } = useTranslation();
  const [choices, setChoices] = useState<UploadChoices>(() => initialChoices(upload, sheet));
  const table = isTableFormat(upload);
  const problem = choicesProblem(choices);
  const places = placeColumns(choices);
  const set = (patch: Partial<UploadChoices>) => setChoices((c) => ({ ...c, ...patch }));

  useEffect(() => {
    onChange(problem === null && sheet.columns.length > 0 ? startRequest(sheet, choices) : null, problem);
  }, [choices, problem, sheet]); // eslint-disable-line react-hooks/exhaustive-deps

  const marks = useMemo(() => {
    const m = new Map<number, string>();
    if (choices.mode === 'lonlat') {
      if (choices.lon != null) m.set(choices.lon, t('uploads.longitude'));
      if (choices.lat != null) m.set(choices.lat, t('uploads.latitude'));
    }
    if (choices.mode === 'wkt' && choices.wkt != null) m.set(choices.wkt, 'WKT');
    return m;
  }, [choices.mode, choices.lon, choices.lat, choices.wkt, t]);

  if (sheet.columns.length === 0) {
    return <p className="text-sm text-gn-secondary">{t('uploads.sheet_empty')}</p>;
  }

  const modes: UploadGeometryMode[] = table ? ['lonlat', 'wkt', 'none'] : ['file', 'none'];

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] gap-x-8 gap-y-4">
      <label className="block text-sm">
        <span className="block text-sm font-medium text-gn-secondary mb-1">{t('uploads.layer_name')}</span>
        <input className={wide} value={choices.layerName} disabled={disabled}
               onChange={(e) => set({ layerName: e.target.value })} />
      </label>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gn-secondary mb-1">{t('uploads.places')}</legend>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {modes.map((m) => (
            <label key={m} className="inline-flex items-center gap-1.5 text-sm text-gn-primary">
              <input type="radio" name={`mode-${upload.id}`} checked={choices.mode === m} disabled={disabled}
                     onChange={() => set({ mode: m })} />
              {t(`uploads.mode_${m}`)}
            </label>
          ))}
        </div>
        {choices.mode === 'none' && <p className="text-xs text-gn-muted">{t('uploads.mode_none_hint')}</p>}
        {choices.mode === 'file' && sheet.sourceGeometryType != null && (
          <UploadLayerInfo sheet={sheet} crs={choices.crs} disabled={disabled} onCrs={(crs) => set({ crs })} />
        )}
        {choices.mode === 'file' && sheet.sourceGeometryType == null && upload.preview?.geometryType && (
          <p className="text-xs text-gn-muted">{t('uploads.file_geometry', { kind: t(`uploads.geometry_${upload.preview.geometryType}`, { defaultValue: upload.preview.geometryType }) })}</p>
        )}
        {(choices.mode === 'lonlat' || choices.mode === 'wkt') && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {choices.mode === 'lonlat' ? (
              <>
                <UploadColumnSelect label={t('uploads.longitude')} columns={sheet.columns} value={choices.lon} disabled={disabled}
                                    onChange={(lon) => set({ lon })} />
                <UploadColumnSelect label={t('uploads.latitude')} columns={sheet.columns} value={choices.lat} disabled={disabled}
                                    onChange={(lat) => set({ lat })} />
              </>
            ) : (
              <div className="sm:col-span-2">
                <UploadColumnSelect label={t('uploads.wkt_column')} columns={sheet.columns} value={choices.wkt} disabled={disabled}
                                    onChange={(wkt) => set({ wkt })} />
              </div>
            )}
            <SelectField label={t('uploads.crs')} value={choices.crs} disabled={disabled} onChange={(e) => set({ crs: e.target.value })}>
              {UPLOAD_CRS_GROUPS.map((g) => (
                <optgroup key={g.key} label={t(`uploads.crs_group_${g.key}`)}>
                  {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </optgroup>
              ))}
            </SelectField>
          </div>
        )}
        {table && choices.mode === 'lonlat' && (sheet.suggestedLon != null || sheet.suggestedLat != null) && (
          <p className="text-xs text-gn-muted">{t('uploads.suggested_hint')}</p>
        )}
      </fieldset>
      </div>

      <UploadSampleTable columns={sheet.columns} marks={marks} />

      <div>
        <p className="text-sm font-medium text-gn-secondary mb-1">{t('uploads.columns')}</p>
        <div className="border border-gn-light rounded-lg divide-y divide-gn-light max-h-80 overflow-y-auto">
          {choices.columns.map((c, i) => {
            const preview = sheet.columns[i];
            const isPlace = places.has(c.position);
            return (
              <div key={c.position} className={`flex items-center gap-3 px-3 py-1.5 ${isPlace ? 'opacity-60' : ''}`}>
                <input type="checkbox" checked={c.include && !isPlace} disabled={disabled || isPlace}
                       title={t('uploads.include')}
                       onChange={(e) => set({ columns: choices.columns.map((x, j) => (j === i ? { ...x, include: e.target.checked } : x)) })} />
                <span className="w-40 shrink-0 truncate text-xs text-gn-muted" title={preview?.header}>
                  {preview?.header || t('uploads.column_n', { n: c.position + 1 })}
                </span>
                {isPlace ? (
                  <span className="text-xs text-gn-blue">{marks.get(c.position)}</span>
                ) : (
                  <>
                    <input className={input} value={c.name} disabled={disabled || !c.include}
                           onChange={(e) => set({ columns: choices.columns.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
                    <select className={input} style={{ width: '8rem' }} value={c.type} disabled={disabled || !c.include}
                            onChange={(e) => set({ columns: choices.columns.map((x, j) => (j === i ? { ...x, type: e.target.value as typeof c.type } : x)) })}>
                      {UPLOAD_COLUMN_TYPES.map((ty) => <option key={ty} value={ty}>{t(`uploads.type_${ty}`)}</option>)}
                    </select>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
};

export default UploadColumnCheck;
